<template>
  <div class="stock-view-page stock-multi-po-receipt">
    <StockDocumentPageHeading
      :title="$t('STOCK.RECEIVING_SESSION.PAGE_TITLE')"
      :code="isCreateMode ? '' : document.code"
      :new-label="isCreateMode ? $t('STOCK.RECEIVING_SESSION.NEW') : ''"
    />

    <v-card v-if="isCreateMode" :loading="isProcessing">
      <v-card-text>
        <p class="mb-4">{{ $t("STOCK.RECEIVING_SESSION.START_HINT") }}</p>
        <v-autocomplete
          v-model="selectedPurchaseOrderIds"
          :items="purchaseOrderOptions"
          :loading="loadingPurchaseOrders"
          :search-input.sync="purchaseOrderSearch"
          item-text="label"
          item-value="id"
          :label="$t('STOCK.RECEIVING_SESSION.SELECT_POS')"
          multiple
          chips
          deletable-chips
          small-chips
          outlined
          dense
          hide-details="auto"
          class="mb-4"
        />
        <v-btn
          color="primary"
          :disabled="selectedPurchaseOrderIds.length < 2"
          :loading="isProcessing"
          @click="startSession"
        >
          {{ $t("STOCK.RECEIVING_SESSION.START_SESSION") }}
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card v-else :loading="isProcessing" :disabled="isProcessing">
      <v-tabs v-model="tab" class="stock-color" left dark>
        <v-tab href="#main">{{ document.code || $t("STOCK.RECEIVING_SESSION.SESSION") }}</v-tab>
        <v-spacer />
        <div class="buttons-tab d-flex align-center flex-wrap pa-2">
          <v-chip v-if="document.status" class="mr-2" :color="statusColor" small dark>
            {{ statusLabel }}
          </v-chip>
          <v-btn v-if="canFinalize" class="mr-2 success" small @click="finalizeReceipt">
            {{ $t("STOCK.RECEIVING_SESSION.FINALIZE_SESSION") }}
          </v-btn>
          <v-btn v-if="canDiscard" class="mr-2 error" small @click="discardReceipt">
            {{ $t("STOCK.DOCUMENTS.DISCARD") }}
          </v-btn>
          <v-btn v-if="canSave" class="mr-2 success" small @click="save">
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </div>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="main">
          <v-card flat>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field
                    :value="organizationLabel"
                    :label="$t('STOCK.WAREHOUSES.ORGANIZATION')"
                    readonly
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    :value="warehouseLabel"
                    :label="$t('STOCK.RECEIPTS.WAREHOUSE')"
                    readonly
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    :value="supplierLabel"
                    :label="$t('STOCK.RECEIPTS.SUPPLIER')"
                    readonly
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <DatePicker
                    v-model="document.action_date"
                    :label="$t('STOCK.RECEIPTS.DATE') + '*'"
                    :disabled="!canSave"
                    without-hint
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="4">
                  <v-card outlined>
                    <v-card-subtitle class="font-weight-bold pb-0">
                      {{ $t("STOCK.RECEIVING_SESSION.ACTIVE_POS") }}
                    </v-card-subtitle>
                    <v-card-text class="pt-2">
                      <div
                        v-for="po in sessionPurchaseOrders"
                        :key="po.id"
                        class="d-flex justify-space-between text-body-2 mb-1"
                      >
                        <router-link :to="{ name: 'stock-purchase-orders-view', params: { id: po.id } }">
                          {{ po.code }}
                        </router-link>
                        <span class="grey--text">{{ po.status }}</span>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="8">
                  <v-alert type="info" dense text class="mb-0">
                    {{ $t("STOCK.RECEIVING_SESSION.WORKFLOW_HINT") }}
                  </v-alert>
                </v-col>
              </v-row>

              <v-alert type="info" dense text class="my-4">
                {{ $t("STOCK.PURCHASE_ORDERS.TOTALS_SUMMARY", {
                  net: formatNum(previewDocumentTotals.total_net),
                  vat: formatNum(previewDocumentTotals.total_vat),
                  gross: formatNum(previewDocumentTotals.total_gross),
                }) }}
              </v-alert>

              <v-expansion-panels v-model="extraCostsPanel" flat class="mb-4">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span class="font-weight-medium">{{ $t("STOCK.RECEIPTS.EXTRA_COSTS") }}</span>
                    <span v-if="extraCosts.length" class="text-caption grey--text ml-2">
                      ({{ extraCosts.length }})
                    </span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-alert type="info" dense text class="mb-3">
                      {{ $t("STOCK.RECEIPTS.EXTRA_COSTS_HINT") }}
                    </v-alert>
                    <v-simple-table dense class="stock-extra-costs-table">
                      <thead>
                        <tr>
                          <th>{{ $t("STOCK.RECEIPTS.EXTRA_CATEGORY") }}</th>
                          <th>{{ $t("GENERAL.LABELS.DESCRIPTION") }}</th>
                          <th>{{ $t("STOCK.RECEIPTS.AMOUNT_NET") }}</th>
                          <th>{{ $t("STOCK.PURCHASE_ORDERS.VAT") }}</th>
                          <th>{{ $t("STOCK.RECEIPTS.ALLOCATION") }}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(extra, extraIndex) in extraCosts" :key="extra._key">
                          <td>
                            <v-select
                              v-model="extra.extra_cost_type_id"
                              :items="extraCostTypeOptions"
                              item-text="label"
                              item-value="id"
                              dense
                              hide-details
                              :disabled="!canSave"
                            />
                          </td>
                          <td>
                            <v-text-field
                              v-model="extra.description"
                              dense
                              hide-details
                              :disabled="!canSave"
                            />
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="extra.amount_net"
                              type="number"
                              min="0"
                              step="0.01"
                              dense
                              hide-details
                              :class="extraAmountNetFieldClass(extra)"
                              :disabled="!canSave"
                              @focus="rememberExtraAmountNet(extra)"
                              @input="onExtraAmountNetChange(extra)"
                            />
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="extra.vat_rate"
                              type="number"
                              suffix="%"
                              dense
                              hide-details
                              :disabled="!canSave"
                            />
                          </td>
                          <td>
                            <v-select
                              v-model="extra.allocation_method_id"
                              :items="allocationMethodOptions"
                              item-text="label"
                              item-value="id"
                              dense
                              hide-details
                              :disabled="!canSave"
                              @change="onExtraAllocationMethodChange(extra)"
                            />
                          </td>
                          <td>
                            <v-btn v-if="canSave" icon small @click="removeExtraCost(extraIndex)">
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                    <v-btn v-if="canSave" class="mt-3" small @click="addExtraCost">
                      <v-icon left small>mdi-plus</v-icon>
                      {{ $t("STOCK.RECEIPTS.ADD_EXTRA_COST") }}
                    </v-btn>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-subheader class="px-0">{{ $t("STOCK.RECEIVING_SESSION.PROCESSED_LOTS") }}</v-subheader>
              <v-alert v-if="!groupedLotEntries.length" type="info" dense text class="mb-3">
                {{ $t("STOCK.RECEIVING_SESSION.NO_LOT_ENTRIES") }}
              </v-alert>
              <v-simple-table v-else dense class="mb-4">
                <thead>
                  <tr>
                    <th>{{ $t("STOCK.PRODUCTS.PRODUCT") }}</th>
                    <th>{{ $t("STOCK.RECEIPTS.LOT_NUMBER") }}</th>
                    <th>{{ $t("STOCK.RECEIVING_SESSION.EXTERNAL_LOT") }}</th>
                    <th>{{ $t("STOCK.RECEIVING_SESSION.PALLET_NUMBER") }}</th>
                    <th>{{ $t("STOCK.RECEIPTS.EXPIRY") }}</th>
                    <th>{{ $t("STOCK.PURCHASE_ORDERS.QTY") }}</th>
                    <th>{{ $t("STOCK.RECEIVING_SESSION.PO_SPLIT") }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="entry in groupedLotEntries" :key="entry.session_lot_entry_key">
                    <td>{{ entry.product_label }}</td>
                    <td>{{ entry.lot_number }}</td>
                    <td>{{ entry.external_lot_number || "-" }}</td>
                    <td>{{ entry.pallet_number || "-" }}</td>
                    <td>{{ entry.expiry_date || "-" }}</td>
                    <td>{{ formatNum(entry.quantity) }}</td>
                    <td>{{ entry.po_split_label }}</td>
                    <td class="text-nowrap">
                      <v-btn x-small text color="primary" @click="editLotEntry(entry)">
                        {{ $t("GENERAL.LABELS.EDIT") }}
                      </v-btn>
                      <v-btn x-small text color="error" @click="removeLotEntry(entry)">
                        {{ $t("GENERAL.LABELS.DELETE") }}
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <v-subheader class="px-0">{{ $t("STOCK.PURCHASE_ORDERS.LINES") }}</v-subheader>
              <div v-if="lines.length" class="stock-document-lines-wrap mb-4">
                <v-simple-table dense class="stock-document-lines">
                  <thead>
                    <tr>
                      <th class="stock-document-lines__sticky stock-document-lines__sticky--index">#</th>
                      <th class="stock-document-lines__sticky stock-document-lines__sticky--product">
                        {{ $t("STOCK.PRODUCTS.PRODUCT") }}
                      </th>
                      <th>{{ $t("STOCK.RECEIPTS.PURCHASE_ORDER") }}</th>
                      <th class="stock-document-lines__col-qty">{{ $t("STOCK.PURCHASE_ORDERS.QTY") }}</th>
                      <th class="stock-document-lines__col-qty">{{ $t("STOCK.COMMON.GROSS_WEIGHT_KG") }}</th>
                      <th class="stock-document-lines__col-money">
                        {{ moneyColumnHeader("STOCK.PURCHASE_ORDERS.UNIT_COST_NET") }}
                      </th>
                      <th class="stock-document-lines__col-money">
                        {{ moneyColumnHeader("STOCK.PURCHASE_ORDERS.LINE_TOTAL_NET") }}
                      </th>
                      <th class="stock-document-lines__col-vat">{{ $t("STOCK.PURCHASE_ORDERS.VAT") }}</th>
                      <th class="stock-document-lines__col-lot">{{ $t("STOCK.RECEIPTS.LOT_NUMBER") }}</th>
                      <th>{{ $t("STOCK.RECEIVING_SESSION.EXTERNAL_LOT") }}</th>
                      <th>{{ $t("STOCK.RECEIVING_SESSION.PALLET_NUMBER") }}</th>
                      <th class="stock-document-lines__col-date">{{ $t("STOCK.RECEIPTS.EXPIRY") }}</th>
                      <th
                        v-for="extra in extraCosts"
                        :key="`hdr-${extra._key}`"
                        class="stock-document-lines__extra-col text-center"
                      >
                        <div class="text-caption font-weight-medium">
                          {{ extraCostColumnTitle(extra) }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ extraCostColumnSubtitle(extra) }}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(line, index) in lines" :key="`line-${index}-${line.stock_product_id}`">
                      <td class="stock-document-lines__sticky stock-document-lines__sticky--index">
                        {{ index + 1 }}
                      </td>
                      <td class="stock-document-lines__sticky stock-document-lines__sticky--product">
                        <div class="font-weight-medium">{{ lineProductLabel(line) }}</div>
                        <div class="text-caption grey--text">{{ lineProductMeta(line) }}</div>
                      </td>
                      <td>{{ line.purchase_order_code || "-" }}</td>
                      <td class="stock-document-lines__col-qty">
                        <StockLineField
                          v-model.number="line.quantity"
                          type="number"
                          :hint="lineUnitCode(line)"
                          :disabled="!canSave"
                          @focus="focusSelectAllInput"
                          @input="onLineQtyOrUnitInput(line, lineQuantityKey)"
                        />
                      </td>
                      <td class="stock-document-lines__col-qty">
                        <StockLineField
                          v-model.number="line.gross_weight_kg"
                          type="number"
                          hint="kg"
                          :disabled="!canSave"
                          @focus="focusSelectAllInput"
                        />
                      </td>
                      <td class="stock-document-lines__col-money">
                        <StockLineField
                          v-model.number="line.unit_cost_net"
                          type="number"
                          :disabled="!canSave"
                          @focus="focusSelectAllInput"
                          @input="onLineQtyOrUnitInput(line, lineQuantityKey)"
                        />
                      </td>
                      <td class="stock-document-lines__col-money">
                        <StockLineField
                          v-model.number="line.line_total_net"
                          type="number"
                          :disabled="!canSave"
                          @focus="focusSelectAllInput"
                          @input="onLineTotalNetInput(line, lineQuantityKey)"
                        />
                      </td>
                      <td class="stock-document-lines__col-vat">
                        <StockLineField
                          v-model.number="line.vat_rate"
                          type="number"
                          hint="%"
                          narrow
                          :disabled="!canSave"
                          @focus="focusSelectAllInput"
                        />
                      </td>
                      <td class="stock-document-lines__col-lot">{{ line.lot_number || "-" }}</td>
                      <td>{{ line.external_lot_number || "-" }}</td>
                      <td>{{ line.pallet_number || "-" }}</td>
                      <td class="stock-document-lines__col-date">{{ line.expiry_date || "-" }}</td>
                      <td
                        v-for="extra in extraCosts"
                        :key="`cell-${index}-${extra._key}`"
                        class="stock-document-lines__extra-col"
                      >
                        <StockLineField
                          v-if="line.stock_product_id && isManualExtraCost(extra)"
                          :key="`manual-${extraAllocFieldKey(line, extra)}`"
                          :value="getManualAllocationAmount(line, extra)"
                          type="number"
                          min="0"
                          step="0.01"
                          alloc
                          :placeholder="manualAllocPlaceholder(line, extra)"
                          :disabled="!canEditExtraLineAllocation(extra)"
                          @focus="focusSelectAllInput"
                          @input="setManualAllocationAmount(line, extra, $event)"
                          @blur="finalizeManualAllocationAmount(line, extra)"
                        />
                        <StockLineField
                          v-else-if="line.stock_product_id"
                          :key="`auto-${extraAllocFieldKey(line, extra)}`"
                          :value="extraLineDisplayValue(line, extra, index)"
                          type="number"
                          alloc
                          readonly
                          disabled
                        />
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <StockProductLineFinder
                :disabled="!canSave"
                @add="onProductSelectedFromFinder"
              />

              <StockDocumentChainLinks
                show-purchase-orders
                :purchase-orders="linkedPurchaseOrders"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <StockReceivingLotAllocationDialog
      v-model="showLotDialog"
      :receipt-id="sessionId"
      :product="lotDialogProduct"
      :edit-entry="lotDialogEditEntry"
      :default-vat-rate="document.default_vat_rate"
      @accepted="onLotEntryAccepted"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockProductLineFinder from "@/views/partials/stock/StockProductLineFinder.vue";
import StockDocumentChainLinks from "@/views/partials/stock/StockDocumentChainLinks.vue";
import StockLineField from "@/views/partials/stock/StockLineField.vue";
import StockReceivingLotAllocationDialog from "@/views/partials/stock/StockReceivingLotAllocationDialog.vue";
import DatePicker from "@/views/partials/form/DatePicker.vue";
import stockDocumentLinesMixin from "@/views/partials/stock/stockDocumentLines.mixin";
import stockDocumentTotalsMixin from "@/views/partials/stock/stockDocumentTotals.mixin";
import stockExtraCostAllocationsMixin from "@/views/partials/stock/stockExtraCostAllocations.mixin";
import stockDocumentContextMixin from "@/views/partials/stock/stockDocumentContext.mixin";
import stockStatusTimelineMixin from "@/views/partials/stock/stockStatusTimeline.mixin";
import { SHOW_SNACK } from "@/store/snack.module";
import { GET_STOCK_PURCHASE_ORDERS } from "@/store/stock/stockPurchaseOrders.module";
import {
  GET_STOCK_RECEIPT,
  UPDATE_STOCK_RECEIPT,
  FINALIZE_STOCK_RECEIPT,
  DISCARD_STOCK_RECEIPT,
  GET_STOCK_EXTRA_COST_TYPE_OPTIONS,
  GET_STOCK_EXTRA_COST_ALLOCATION_METHOD_OPTIONS,
} from "@/store/stock/stockReceipts.module";
import {
  START_STOCK_RECEIVING_SESSION,
  GET_STOCK_RECEIVING_SESSION,
  GET_STOCK_RECEIVING_SESSION_CONTEXT,
  REMOVE_STOCK_RECEIVING_LOT_ENTRY,
} from "@/store/stock/stockReceivingSessions.module";

export default {
  name: "StockMultiPoReceiptSession",
  components: {
    StockDocumentPageHeading,
    StockProductLineFinder,
    StockDocumentChainLinks,
    StockLineField,
    StockReceivingLotAllocationDialog,
    DatePicker,
  },
  mixins: [
    stockDocumentLinesMixin,
    stockDocumentTotalsMixin,
    stockExtraCostAllocationsMixin,
    stockDocumentContextMixin,
    stockStatusTimelineMixin,
  ],
  data() {
    return {
      tab: "main",
      isProcessing: false,
      loadingPurchaseOrders: false,
      purchaseOrderSearch: "",
      purchaseOrderOptions: [],
      selectedPurchaseOrderIds: [],
      sessionPurchaseOrders: [],
      document: {
        code: "",
        status: "draft",
        organization_id: null,
        destination_warehouse_id: null,
        source_company_id: null,
        action_date: new Date().toISOString().slice(0, 10),
        default_vat_rate: 20,
        currency_code: "EUR",
        total_net: 0,
        total_vat: 0,
        total_gross: 0,
      },
      lines: [],
      extraCosts: [],
      extraCostTypeOptions: [],
      allocationMethodOptions: [],
      extraCostsPanel: null,
      showLotDialog: false,
      lotDialogProduct: null,
      lotDialogEditEntry: null,
      lineQuantityKey: "quantity",
    };
  },
  computed: {
    isCreateMode() {
      return this.$route.name === "stock-receipts-multi-po-create";
    },
    sessionId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null;
    },
    linkedPurchaseOrders() {
      return this.document.purchase_orders || this.sessionPurchaseOrders || [];
    },
    organizationLabel() {
      return this.document.organization?.label || this.document.organization?.name || "-";
    },
    warehouseLabel() {
      return this.document.destination_warehouse?.label
        || this.document.destination_warehouse?.name
        || "-";
    },
    supplierLabel() {
      return this.document.source_company?.label || this.document.source_company?.name || "-";
    },
    canSave() {
      return this.hasPermissions("stock-receipts-edit") && this.document.status === "draft";
    },
    canFinalize() {
      return (
        this.sessionId
        && this.document.status === "draft"
        && this.lines.length > 0
        && this.hasPermissions("stock-receipts-finalize")
      );
    },
    productLines() {
      return (this.lines || []).filter((l) => l.stock_product_id);
    },
    canDiscard() {
      return this.sessionId && this.document.status === "draft" && this.hasPermissions("stock-receipts-cancel");
    },
    statusColor() {
      const map = {
        draft: "grey",
        expected: "orange",
        finalized: "success",
        reversed: "purple",
        cancelled: "error",
      };
      return map[this.document.status] || "grey";
    },
    statusLabel() {
      const key = `STOCK.STATUSES.RECEIPT.${(this.document.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.document.status;
    },
    groupedLotEntries() {
      const groups = {};
      (this.lines || []).forEach((line) => {
        const key = line.session_lot_entry_key;
        if (!key) {
          return;
        }
        if (!groups[key]) {
          groups[key] = {
            session_lot_entry_key: key,
            product_label: this.lineProductLabel(line),
            stock_product: line.stock_product,
            stock_product_id: line.stock_product_id,
            lot_number: line.lot_number,
            external_lot_number: line.external_lot_number,
            pallet_number: line.pallet_number,
            expiry_date: line.expiry_date,
            production_date: line.production_date,
            quality_status: line.quality_status || "free",
            quality_hold_reason: line.quality_hold_reason || "",
            quantity: 0,
            unit_cost_net: line.unit_cost_net,
            vat_rate: line.vat_rate,
            allocations: [],
          };
        }
        groups[key].quantity += Number(line.quantity || 0);
        groups[key].allocations.push({
          purchase_order_item_id: line.purchase_order_item_id,
          purchase_order_code: line.purchase_order_code,
          quantity: Number(line.quantity || 0),
        });
      });

      return Object.values(groups).map((entry) => ({
        ...entry,
        po_split_label: entry.allocations
          .map((row) => `${row.purchase_order_code || "?"}: ${this.formatNum(row.quantity)}`)
          .join(", "),
      }));
    },
  },
  watch: {
    "$route.params.id"(id) {
      if (id && !this.isCreateMode) {
        this.bootstrapSession();
      }
    },
    purchaseOrderSearch: debounce(function () {
      this.loadPurchaseOrderOptions();
    }, 300),
  },
  mounted() {
    if (this.isCreateMode) {
      this.loadPurchaseOrderOptions();
      return;
    }
    if (this.sessionId) {
      this.bootstrapSession();
    }
  },
  methods: {
    async loadPurchaseOrderOptions() {
      this.loadingPurchaseOrders = true;
      try {
        const response = await this.$store.dispatch(GET_STOCK_PURCHASE_ORDERS, {
          page: 1,
          itemsPerPage: 50,
          search: this.purchaseOrderSearch || "",
          filters: {
            status: ["confirmed", "partially_received"],
          },
        });
        this.purchaseOrderOptions = (response.data || []).map((po) => ({
          id: po.id,
          label: `${po.code} · ${po.supplier_company?.label || po.supplier_company?.name || ""}`,
        }));
      } finally {
        this.loadingPurchaseOrders = false;
      }
    },
    async startSession() {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(START_STOCK_RECEIVING_SESSION, {
          purchase_order_ids: this.selectedPurchaseOrderIds,
        });
        await this.$router.replace({
          name: "stock-receipts-multi-po-view",
          params: { id: data.id },
        });
        await this.applyDocument(data);
        await this.loadSessionContext();
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async bootstrapSession() {
      this.isProcessing = true;
      try {
        await this.loadExtraCostOptions();
        const data = await this.$store.dispatch(GET_STOCK_RECEIVING_SESSION, this.sessionId);
        await this.applyDocument(data);
        await this.loadSessionContext();
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async loadSessionContext() {
      if (!this.sessionId) {
        return;
      }
      const context = await this.$store.dispatch(
        GET_STOCK_RECEIVING_SESSION_CONTEXT,
        this.sessionId
      );
      this.sessionPurchaseOrders = context.purchase_orders || [];
    },
    async loadExtraCostOptions() {
      const [types, methods] = await Promise.all([
        this.$store.dispatch(GET_STOCK_EXTRA_COST_TYPE_OPTIONS),
        this.$store.dispatch(GET_STOCK_EXTRA_COST_ALLOCATION_METHOD_OPTIONS),
      ]);
      this.extraCostTypeOptions = types || [];
      this.allocationMethodOptions = methods || [];
    },
    mapExtraCostsFromApi(apiExtras) {
      return (apiExtras || []).map((e) => ({
        _key: this.nextExtraCostKey(),
        id: e.id,
        extra_cost_type_id: e.extra_cost_type_id || e.extra_cost_type?.id,
        allocation_method_id: e.allocation_method_id || e.allocation_method?.id,
        extra_cost_type: e.extra_cost_type,
        allocation_method: e.allocation_method,
        description: e.description,
        amount_net: Number(e.amount_net),
        vat_rate: e.vat_rate,
        line_allocations: e.line_allocations || [],
      }));
    },
    applyExtraAllocationsToLines(mappedExtras) {
      const productLines = (this.lines || []).filter((l) => l.stock_product_id);
      productLines.forEach((line, lineIndex) => {
        const allocations = {};
        mappedExtras.forEach((extra) => {
          const row = (extra.line_allocations || []).find(
            (a) => Number(a.line_index) === lineIndex
          );
          if (row) {
            allocations[extra._key] = Number(row.amount_net);
          }
        });
        this.$set(line, "extra_allocations", allocations);
      });
    },
    async applyDocument(data) {
      this.document = {
        ...this.document,
        ...data,
      };
      this.lines = (data.items || []).map((item) => ({
        ...this.mapLineFromApi(item, data, this.lineQuantityKey),
        lot_number: item.lot_number || "",
        external_lot_number: item.external_lot_number || "",
        pallet_number: item.pallet_number || "",
        production_date: item.production_date,
        expiry_date: item.expiry_date,
        quality_status: item.quality_status === "blocked" ? "blocked" : "free",
        quality_hold_reason: item.quality_hold_reason || "",
        purchase_order_item_id: item.purchase_order_item_id,
        purchase_order_code: item.purchase_order?.code || null,
        session_lot_entry_key: item.session_lot_entry_key,
        gross_weight_kg: item.gross_weight_kg,
        gross_to_net_ratio: item.gross_to_net_ratio,
        weight_basis: item.weight_basis,
        extra_allocations: {},
      }));
      const mappedExtras = this.mapExtraCostsFromApi(data.extra_costs);
      this.extraCosts = mappedExtras;
      this.applyExtraAllocationsToLines(mappedExtras);
    },
    payload() {
      this.finalizeAllManualAllocations();
      return {
        organization_id: this.document.organization_id,
        destination_warehouse_id: this.document.destination_warehouse_id,
        source_company_id: this.document.source_company_id,
        action_date: this.document.action_date,
        default_vat_rate: this.document.default_vat_rate,
        currency_code: this.document.currency_code,
        supplier_invoice_no: this.document.supplier_invoice_no,
        supplier_invoice_date: this.document.supplier_invoice_date,
        items: this.lines
          .filter((l) => l.stock_product_id)
          .map((line, index) => ({
            line_no: index + 1,
            stock_product_id: line.stock_product_id,
            quantity: line.quantity,
            unit_id: line.unit_id,
            gross_weight_kg: line.gross_weight_kg,
            unit_cost_net: line.unit_cost_net,
            vat_rate: line.vat_rate ?? this.document.default_vat_rate,
            lot_number: line.lot_number,
            external_lot_number: line.external_lot_number,
            pallet_number: line.pallet_number,
            production_date: line.production_date,
            expiry_date: line.expiry_date,
            quality_status: line.quality_status === "blocked" ? "blocked" : "free",
            quality_hold_reason:
              line.quality_status === "blocked" ? line.quality_hold_reason || null : null,
            purchase_order_item_id: line.purchase_order_item_id,
            session_lot_entry_key: line.session_lot_entry_key,
          })),
        extra_costs: this.buildExtraCostsPayload(
          this.extraCosts,
          this.productLines,
          this.lineQuantityKey
        ),
      };
    },
    async save() {
      if (!this.sessionId) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(UPDATE_STOCK_RECEIPT, {
          id: this.sessionId,
          params: this.payload(),
        });
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async finalizeReceipt() {
      await this.save();
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(FINALIZE_STOCK_RECEIPT, this.sessionId);
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.RECEIPTS.FINALIZED_MSG"),
          color: "success",
        });
        await this.$router.push({
          name: "stock-receipts-view",
          params: { id: this.sessionId },
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async discardReceipt() {
      if (!confirm(this.$t("STOCK.DOCUMENTS.CONFIRM_DISCARD"))) {
        return;
      }
      this.isProcessing = true;
      try {
        await this.$store.dispatch(DISCARD_STOCK_RECEIPT, this.sessionId);
        this.$router.push({ name: "stock-movements" });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    onProductSelectedFromFinder(product) {
      this.lotDialogEditEntry = null;
      this.lotDialogProduct = product;
      this.showLotDialog = true;
    },
    editLotEntry(entry) {
      this.lotDialogProduct = entry.stock_product || {
        id: entry.stock_product_id,
        display_name: entry.product_label,
      };
      this.lotDialogEditEntry = entry;
      this.showLotDialog = true;
    },
    async removeLotEntry(entry) {
      if (!confirm(this.$t("STOCK.RECEIVING_SESSION.CONFIRM_REMOVE_LOT_ENTRY"))) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(REMOVE_STOCK_RECEIVING_LOT_ENTRY, {
          receiptId: this.sessionId,
          sessionLotEntryKey: entry.session_lot_entry_key,
        });
        await this.applyDocument(data);
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async onLotEntryAccepted(data) {
      await this.applyDocument(data);
      await this.loadSessionContext();
    },
    defaultExtraCostTypeId() {
      const transport = this.extraCostTypeOptions.find((t) => t.slug === "transport");
      return transport ? transport.id : this.extraCostTypeOptions[0]?.id;
    },
    defaultAllocationMethodId(slug = "by_value") {
      const method = this.allocationMethodOptions.find((m) => m.slug === slug);
      return method ? method.id : this.allocationMethodOptions[0]?.id;
    },
    addExtraCost() {
      this.extraCosts.push({
        _key: this.nextExtraCostKey(),
        extra_cost_type_id: this.defaultExtraCostTypeId(),
        allocation_method_id: this.defaultAllocationMethodId(),
        description: "",
        amount_net: 0,
        vat_rate: null,
        line_allocations: [],
      });
      this.extraCostsPanel = 0;
    },
    removeExtraCost(index) {
      const extra = this.extraCosts[index];
      if (extra) {
        this.clearExtraAllocationsOnLines(extra._key);
      }
      this.extraCosts.splice(index, 1);
    },
    moneyColumnHeader(i18nKey) {
      const label = this.$t(i18nKey);
      const ccy = this.document.currency_code;
      return ccy ? `${label} · ${ccy}` : label;
    },
    formatNum(v) {
      return Number(v || 0).toFixed(2);
    },
    extraAmountNetFieldClass(extra) {
      if (!this.isManualExtraCost(extra)) {
        return "";
      }
      if (this.manualAllocationIsOverTotal(extra)) {
        return "error--text";
      }
      if (this.manualAllocationIsUnderTotal(extra)) {
        return "warning--text";
      }
      return "";
    },
    onExtraAllocationMethodChange(extra) {
      this.clearExtraAllocationsOnLines(extra._key);
    },
    extraCostTypeLabel(extra) {
      const type = this.extraCostTypeOptions.find(
        (t) => Number(t.id) === Number(extra.extra_cost_type_id)
      );
      return type?.label || type?.name || "";
    },
    extraCostColumnTitle(extra) {
      return this.extraCostTypeLabel(extra) || this.$t("STOCK.RECEIPTS.EXTRA_SERVICE");
    },
    extraCostColumnSubtitle(extra) {
      const method = this.allocationMethodOptions.find(
        (m) => Number(m.id) === Number(extra.allocation_method_id)
      );
      const methodName = method?.label || method?.name || "";
      const amount = this.formatNum(extra.amount_net);
      const ccy = this.document.currency_code || "";
      return [methodName, amount, ccy].filter(Boolean).join(" · ");
    },
    extraAllocFieldKey(line, extra) {
      const productIndex = this.productLineIndex(line);
      return [
        extra._key,
        extra.allocation_method_id,
        extra.amount_net,
        line.quantity,
        line.unit_cost_net,
        line.line_total_net,
        productIndex,
      ].join("-");
    },
    extraLineDisplayValue(line, extra) {
      return this.previewExtraLineAmount(
        this.productLines,
        extra,
        this.productLineIndex(line),
        this.lineQuantityKey
      );
    },
    productLineIndex(line) {
      return this.productLines.findIndex((l) => l === line);
    },
  },
};
</script>

<style scoped>
.stock-document-lines-wrap {
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}
.stock-document-lines {
  border-collapse: separate;
  border-spacing: 0;
}
.stock-document-lines th,
.stock-document-lines td {
  vertical-align: top !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.stock-document-lines thead th {
  background: #f5f5f5;
  white-space: nowrap;
}
.stock-document-lines__sticky--index {
  position: sticky;
  left: 0;
  z-index: 2;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  background: #fff;
  text-align: center;
}
.stock-document-lines__sticky--product {
  position: sticky;
  left: 44px;
  z-index: 2;
  min-width: 180px;
  background: #fff;
}
.stock-document-lines__extra-col {
  min-width: 88px;
  max-width: 120px;
}
</style>
